/** How long a newly revealed word takes to fade in. */
export const REVEAL_FADE_MS = 400

/**
 * Steps of the fade: highlights `chat-reveal-0` to `chat-reveal-11`, each
 * drawing its text at one alpha (assistant-ui.css).
 */
const STEPS = 12

/**
 * How long a word stays at one step. Reveal times snap to this grid, so every
 * fading word changes step on the same frames and the fade is redrawn once a
 * step, not once a frame.
 */
const STEP_MS = REVEAL_FADE_MS / STEPS

/** Text under these keeps its own colour, which a highlight would replace. */
const UNFADED =
  'a, code, pre, table, source-citation, table-cite, web-citation, web-link'

/** Whether an element is under one of {@link UNFADED}, asked once per element. */
const unfaded = new WeakMap<Element, boolean>()

const isUnfaded = (element: Element): boolean => {
  let answer = unfaded.get(element)
  if (answer === undefined) {
    answer = element.closest(UNFADED) !== null
    unfaded.set(element, answer)
  }
  return answer
}

/**
 * One highlight per fade step, shared by every streaming part on the page, or
 * null where the browser has no CSS Custom Highlight API.
 */
const fadeSteps = (): Highlight[] | null => {
  if (typeof CSS === 'undefined' || !('highlights' in CSS)) return null
  return Array.from({ length: STEPS }, (_, step) => {
    const name = `chat-reveal-${step}`
    const highlight = CSS.highlights.get(name) ?? new Highlight()
    CSS.highlights.set(name, highlight)
    return highlight
  })
}

interface Reveal {
  /** Frame time the text up to `end` was first drawn. */
  at: number
  end: number
}

/**
 * Fades in the words of a streaming answer as they are first drawn, by
 * drawing each word drawn in the last {@link REVEAL_FADE_MS} through a
 * highlight at the alpha its age has reached. Nothing in the DOM or in React
 * changes: the text is only drawn fainter. Only the active tail fades: a
 * block that commits mid-fade shows its last words at once, because redrawing
 * committed blocks repaints far more of a long answer than the tail.
 *
 * Offsets count the answer's drawn text, not its markdown: a citation tag
 * drawn as a pill, or a link drawn as its words, would otherwise move every
 * word before it by the length of its markup.
 * Owned by one streaming text part and written by its reveal loop.
 */
export class RevealFade {
  /** Everything before this offset has finished fading. */
  private settledEnd = 0
  /** The frame time of the last paint. */
  private now = 0
  /** Offset of the end of the text the DOM shows. */
  private end = 0
  /** The next render shows its text at once: the first one, or one after a jump. */
  private jumped = true
  /** How many of the committed blocks' elements {@link committedLength} has counted. */
  private committedCount = 0
  private committedLength = 0
  private reveals: Reveal[] = []
  /** When the drawn fade next changes, or 0 when the DOM changed since it was drawn. */
  private redrawAt = 0
  /**
   * The ranges this part has in each step's highlight. Static: a live Range
   * is updated by every DOM change until it is collected, so one per word per
   * repaint would tax every React commit.
   */
  private ranges: StaticRange[][] = Array.from({ length: STEPS }, () => [])
  private frame = 0

  private constructor(private readonly steps: Highlight[]) {}

  /** A fade for a part, or null when the user asks for less motion or the browser can't. */
  static create(): RevealFade | null {
    if (
      typeof window === 'undefined' ||
      (typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    )
      return null
    const highlights = fadeSteps()
    return highlights === null ? null : new RevealFade(highlights)
  }

  /** Whether some word is still fading or still drawn faint. */
  get fading(): boolean {
    return (
      this.settledEnd < this.end || this.ranges.some((step) => step.length > 0)
    )
  }

  /** The text was replaced: what the next render shows, it shows at once. */
  reset(): void {
    this.jump()
    this.reveals = []
    this.committedCount = 0
    this.committedLength = 0
    this.clear()
  }

  /** The reveal skipped ahead: the next render shows its text at once. */
  jump(): void {
    this.jumped = true
  }

  /**
   * The active tail `root` was rendered at frame time `now`: the text it newly
   * shows starts to fade, and the next paint redraws.
   */
  rendered(root: HTMLElement, now: number): void {
    const end =
      this.committedText(root.previousElementSibling) +
      (root.textContent?.length ?? 0)
    if (this.jumped) {
      this.jumped = false
      this.reveals = []
      this.settledEnd = end
    } else if (end > this.end) {
      const at = Math.floor(now / STEP_MS) * STEP_MS
      const last = this.reveals.at(-1)
      if (last?.at === at) last.end = end
      else this.reveals.push({ at, end })
    }
    // Markup that collapses as it closes (a citation tag into its pill) can
    // shorten the drawn text: nothing past its end is left to fade.
    for (const reveal of this.reveals) reveal.end = Math.min(reveal.end, end)
    this.settledEnd = Math.min(this.settledEnd, end)
    this.end = end
    this.redrawAt = 0
  }

  /**
   * Redraws the fading words under `root`, the active tail, when a word has
   * changed step or the DOM has changed.
   */
  paint(root: HTMLElement | null, now: number): void {
    if (now < this.redrawAt) return
    this.redrawAt = Infinity
    this.removeRanges()
    this.settle(now)
    if (root === null || this.settledEnd >= this.end) return
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
    let fromEnd = 0
    for (
      let node = walker.lastChild() as Text | null;
      node !== null && this.end - fromEnd > this.settledEnd;
      node = walker.previousNode() as Text | null
    ) {
      const parent = node.parentElement
      if (
        parent !== null &&
        !isUnfaded(parent) &&
        this.paintText(node, this.end - fromEnd, now)
      )
        return
      fromEnd += node.data.length
    }
  }

  /** Keeps redrawing after the reveal loop stops, until every word has faded in. */
  paintUntilSettled(root: () => HTMLElement | null): void {
    cancelAnimationFrame(this.frame)
    const step = (now: number) => {
      this.paint(root(), now)
      this.frame = this.fading ? requestAnimationFrame(step) : 0
    }
    this.frame = requestAnimationFrame(step)
  }

  /** Draws every word at full strength and stops redrawing. */
  clear(): void {
    cancelAnimationFrame(this.frame)
    this.frame = 0
    this.removeRanges()
  }

  /**
   * Length of the text the committed blocks draw. They only ever append, so
   * each element is counted once, when it first appears.
   */
  private committedText(committed: Element | null): number {
    const blocks = committed?.children
    if (blocks === undefined || blocks.length < this.committedCount) {
      this.committedCount = 0
      this.committedLength = 0
    }
    for (
      ;
      blocks !== undefined && this.committedCount < blocks.length;
      this.committedCount += 1
    )
      this.committedLength +=
        blocks[this.committedCount].textContent?.length ?? 0
    return this.committedLength
  }

  private settle(now: number): void {
    this.now = now
    let settled = 0
    while (
      settled < this.reveals.length &&
      this.reveals[settled].at <= now - REVEAL_FADE_MS
    ) {
      this.settledEnd = this.reveals[settled].end
      settled += 1
    }
    if (settled > 0) this.reveals.splice(0, settled)
  }

  /** The frame time `offset` was revealed, or null once it has settled. */
  private revealedAt(offset: number): number | null {
    if (offset < this.settledEnd) return null
    const reveal = this.reveals.find((step) => step.end > offset)
    if (reveal === undefined || reveal.at <= this.now - REVEAL_FADE_MS)
      return null
    return reveal.at
  }

  /**
   * Puts a text node's still-fading words in the highlight for their age,
   * walking back from its end; returns true at the first settled word, which
   * ends the walk. A word's offset is its distance from the end of the
   * drawn answer.
   */
  private paintText(text: Text, end: number, now: number): boolean {
    const value = text.data
    const firstWord = value.search(/\S/)
    if (firstWord === -1) return false
    let wordEnd = value.length
    let runEnd = wordEnd
    let runStep = -1
    while (wordEnd > 0) {
      let start = wordEnd
      while (start > 0 && isSpace(value, start - 1)) start -= 1
      while (start > 0 && !isSpace(value, start - 1)) start -= 1
      if (start <= firstWord) start = 0
      const at = this.revealedAt(end - (value.length - start))
      if (at === null) {
        this.addRange(runStep, text, wordEnd, runEnd)
        return true
      }
      const step = Math.min(
        STEPS - 1,
        Math.floor(Math.max(0, now - at) / STEP_MS),
      )
      this.redrawAt = Math.min(this.redrawAt, at + (step + 1) * STEP_MS)
      if (step !== runStep) {
        this.addRange(runStep, text, wordEnd, runEnd)
        runStep = step
        runEnd = wordEnd
      }
      wordEnd = start
    }
    this.addRange(runStep, text, 0, runEnd)
    return false
  }

  private addRange(step: number, text: Text, start: number, end: number): void {
    if (step < 0 || start >= end) return
    const range = new StaticRange({
      startContainer: text,
      startOffset: start,
      endContainer: text,
      endOffset: end,
    })
    this.steps[step].add(range)
    this.ranges[step].push(range)
  }

  private removeRanges(): void {
    this.ranges.forEach((ranges, step) => {
      for (const range of ranges) this.steps[step].delete(range)
      ranges.length = 0
    })
  }
}

const isSpace = (value: string, index: number): boolean =>
  /\s/.test(value[index])
